import { useEffect, useState } from 'react';
import { organisationsApi } from '../lib/api';

// One request per organisation per page load, shared by every OrgLogo that
// shows it (the list, the page hero, linked-organisation cards). A miss is
// cached too, so an organisation without a usable icon isn't asked again.
const iconCache = new Map();

const loadIcon = (orgId, website) => {
  const key = `${orgId}|${website}`;
  if (!iconCache.has(key)) {
    iconCache.set(
      key,
      organisationsApi
        .getIcon(orgId)
        .then((res) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(res.data) : null))
        .catch(() => null),
    );
  }
  return iconCache.get(key);
};

const useOrgIcon = (orgId, website) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    setSrc(null);
    if (!orgId || !(website || '').trim()) return undefined;
    let live = true;
    loadIcon(orgId, website).then((url) => { if (live) setSrc(url); });
    return () => { live = false; };
  }, [orgId, website]);
  return src;
};

/**
 * An organisation's avatar. With a website on file it shows that site's own
 * icon — fetched and re-encoded by our backend (`GET /organisations/{id}/icon`,
 * services/org_icons.py), never by the browser from a third party — with the
 * organisation-type icon as a small badge in the corner so the kind (school,
 * council, doctor…) still reads at a glance. Without one, or while it loads,
 * or if the site has no usable icon, it's the type icon on the avatar fill.
 *
 * Props:
 * - orgId, website: which organisation; no icon is requested without both
 * - FallbackIcon: the organisation type's lucide icon (also the badge)
 * - size: pixel size of the avatar (default 48)
 * - className: extra classes for the outer wrapper
 */
export const OrgLogo = ({ orgId, website, FallbackIcon, size = 48, className = '' }) => {
  const src = useOrgIcon(orgId, website);
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [src]);

  const boxStyle = { width: `${size}px`, height: `${size}px` };

  if (src && !errored) {
    const badge = Math.max(18, Math.round(size * 0.4));
    const badgeIcon = Math.round(badge * 0.6);
    return (
      <div className={`relative flex-shrink-0 ${className}`} style={boxStyle} data-testid="org-logo">
        <div className="w-full h-full rounded-lg overflow-hidden bg-surface border border-rule flex items-center justify-center">
          <img
            src={src}
            alt=""
            width={size}
            height={size}
            className="w-full h-full object-contain p-[12%]"
            onError={() => setErrored(true)}
          />
        </div>
        {FallbackIcon ? (
          <span
            className="absolute -bottom-1 -right-1 rounded-full bg-surface border border-rule flex items-center justify-center"
            style={{ width: `${badge}px`, height: `${badge}px` }}
            aria-hidden="true"
            data-testid="org-logo-badge"
          >
            <FallbackIcon className="text-ink-70 stroke-[1.75]" style={{ width: `${badgeIcon}px`, height: `${badgeIcon}px` }} />
          </span>
        ) : null}
      </div>
    );
  }

  const iconSize = Math.round(size * 0.5);
  return (
    <div
      className={`rounded-lg bg-avatar flex items-center justify-center flex-shrink-0 ${className}`}
      style={boxStyle}
      data-testid="org-logo-fallback"
    >
      {FallbackIcon ? (
        <FallbackIcon className="text-ink-70 stroke-[1.5]" style={{ width: `${iconSize}px`, height: `${iconSize}px` }} />
      ) : null}
    </div>
  );
};

export default OrgLogo;
